import React, { useState, useEffect, useCallback, useRef } from 'react';
import usePersistentState from '../utils/usePersistentState';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { schedulerAPI, settingsAPI, clientAPI } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Button, Badge, Spinner, EmptyState, ConfirmModal } from '../components/UI';

// ─────────────────────────────────────────────────────────────────────────────
// Schedulers — SuperAdmin monitoring page for every scheduler in the app:
//   • DB backup job   (app-wide; every6h / every12h / daily / weekly)
//   • Client daily-report schedulers (one per client, fire at HH:MM server time)
// Read-only overview + two safe actions that reuse existing endpoints:
// "Run backup now" and per-client "Send report now".
//
// All schedule times are SERVER-local (the schedulers compare against the
// server clock). Countdowns are computed against the server clock too, via the
// serverNow offset returned by the API — so a browser in another timezone (or
// with a skewed clock) still shows correct "in 2h 14m" values.
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAYS   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FREQ_LABEL = { every6h: 'Every 6 hours', every12h: 'Every 12 hours', daily: 'Daily', weekly: 'Weekly' };

const pad2 = (n) => String(n).padStart(2, '0');

// "17:55" (24h) → "5:55 PM" for display; keeps null-safety for unset times.
const fmtHHMM = (hhmm) => {
  const m = String(hhmm || '').match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

// Absolute display for an ISO instant, e.g. "Wed, 5 Aug · 5:55 PM".
const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

// Countdown from milliseconds → "in 2h 14m" / "in 3d 4h" / "due now".
const fmtIn = (ms) => {
  if (ms == null) return null;
  if (ms < 60000) return 'due now';
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60), d = Math.floor(h / 24);
  if (d > 0) return `in ${d}d ${h % 24}h`;
  if (h > 0) return `in ${h}h ${mins % 60}m`;
  return `in ${mins}m`;
};

const fmtAgo = (iso, serverMs) => {
  if (!iso) return null;
  const ms = serverMs - new Date(iso).getTime();
  if (isNaN(ms) || ms < 0) return 'just now';
  if (ms < 60000) return 'just now';
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60), d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  return `${mins}m ago`;
};

// UTC offset label, e.g. "UTC+5:30" for 330 minutes.
const fmtOffset = (mins) => {
  if (typeof mins !== 'number') return '';
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  return `UTC${sign}${Math.floor(abs / 60)}:${pad2(abs % 60)}`;
};

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />
  </svg>
);

// Stat tile for the summary row.
function StatCard({ label, value, sub, accent, isDark }) {
  return (
    <div className="rounded-xl p-4 flex-1 min-w-[180px]" style={{
      background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-card)',
    }}>
      <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-xl font-bold truncate" style={{ color: accent || 'var(--text-primary)' }}>{value}</p>
      {sub && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-tertiary)' }} title={typeof sub === 'string' ? sub : undefined}>{sub}</p>}
    </div>
  );
}

function SectionCard({ title, icon, children, action }) {
  return (
    <div className="rounded-xl" style={{
      background: 'var(--surface-1)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-card)',
    }}>
      <div className="flex items-center justify-between gap-2 px-5 py-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <span>{icon}</span>{title}
        </h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-right min-w-0" style={{ color: 'var(--text-secondary)' }}>{children}</span>
    </div>
  );
}

export default function Schedulers() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  // 'backup' | 'reports' — the two scheduler families, one tab each.
  // Kept for this tab — survives leaving the page and coming back.
  const [activeTab, setActiveTab] = usePersistentState('schedulers:tab', 'backup');
  const [runningBackup, setRunningBackup] = useState(false);
  const [sendingReport, setSendingReport] = useState(null);   // clientId while sending
  // Pending confirmation: { type: 'backup' } | { type: 'report', client }
  const [confirm, setConfirm] = useState(null);

  // serverDelta = serverNow − browserNow at fetch time. Countdowns below use
  // (Date.now() + serverDelta) so browser clock skew can't distort them.
  const serverDelta = useRef(0);

  // Re-render every 30s so countdowns tick without refetching.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data: d } = await schedulerAPI.getAll();
      serverDelta.current = new Date(d.serverNow).getTime() - Date.now();
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load schedulers.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const serverMs = Date.now() + serverDelta.current;
  const untilMs  = (iso) => (iso ? new Date(iso).getTime() - serverMs : null);

  // Set in the effect body (not only cleared in cleanup) so StrictMode's
  // mount → unmount → mount in dev builds leaves it true.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // Poll the backup job every 3 s until it is no longer running. A failed poll
  // is transient (the DB or proxy may be busy) — keep polling; it is never a
  // failed backup. Resolves null if the page is left, or { status: 'unknown' }
  // if a newer job has replaced ours as the latest one.
  const waitForBackupJob = async (jobId) => {
    while (mounted.current) {
      await new Promise(r => setTimeout(r, 3000));
      if (!mounted.current) break;
      try {
        const { data: d } = await settingsAPI.getDbBackupJob();
        const j = d.job;
        if (!j || (jobId && j.id !== jobId)) return { status: 'unknown' };
        if (j.status !== 'running') return j;
      } catch { /* transient — retry on the next tick */ }
    }
    return null;
  };

  // A backup runs as a background job: the POST answers 202 { job } at once,
  // or 409 (with a message) if a backup/restore is already running.
  const runBackupNow = async () => {
    setRunningBackup(true);
    try {
      const { data: d } = await settingsAPI.runDbBackup();
      if (!d.job) {
        toast.success(d.message || 'Backup created.');
        load(true);
        return;
      }
      toast('Backup started — this can take a few minutes.');
      load(true);
      const j = await waitForBackupJob(d.job.id);
      if (!j) return;   // page left — nothing to announce
      if (j.status === 'succeeded') toast.success('Backup created.');
      else if (j.status === 'failed') toast.error(j.error || 'Backup failed.');
      else toast('Backup finished — see the last result below.');
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Backup could not be started.');
    } finally { if (mounted.current) setRunningBackup(false); }
  };

  const sendReportNow = async (client) => {
    setSendingReport(client.clientId);
    try {
      const { data: d } = await clientAPI.sendReport(client.clientId);
      toast.success(d.message || 'Report sent.');
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send report.');
    } finally { setSendingReport(null); }
  };

  if (loading) return <div className="py-24"><Spinner size="lg" /></div>;
  if (!data)   return <EmptyState icon="⏰" title="Couldn't load schedulers" message="Try refreshing the page." action={<Button onClick={() => load()}>Retry</Button>} />;

  const { dbBackup, clientReports } = data;
  const activeReports = clientReports.filter(c => c.enabled);

  // Merged upcoming-runs timeline (backup + every enabled report), soonest
  // first. Feeds the "Next run" stat card, which spans both tabs.
  const upcoming = [
    ...(dbBackup.nextRunAt ? [{ key: 'backup', label: 'Database backup', type: 'Backup', at: dbBackup.nextRunAt }] : []),
    ...activeReports.filter(c => c.nextRunAt).map(c => ({
      key: `report-${c.clientId}`, label: `Daily report — ${c.clientName}`, type: 'Report', at: c.nextRunAt,
    })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  const nextUp = upcoming[0] || null;

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Schedulers</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Every automated job in the app — schedules, next runs, and last results.
            All times are server time ({fmtOffset(data.timezoneOffsetMinutes)}).
          </p>
        </div>
        <Button variant="secondary" onClick={() => load()}>↻ Refresh</Button>
      </div>

      {/* ── Summary ── */}
      <div className="flex flex-wrap gap-3">
        <StatCard isDark={isDark} label="Active schedulers"
          value={(dbBackup.config.enabled ? 1 : 0) + activeReports.length}
          sub={`${dbBackup.config.enabled ? '1 backup job' : 'backup off'} · ${activeReports.length} client report${activeReports.length === 1 ? '' : 's'}`} />
        <StatCard isDark={isDark} label="Next run"
          value={nextUp ? fmtIn(untilMs(nextUp.at)) : '—'}
          sub={nextUp ? `${nextUp.label} · ${fmtWhen(nextUp.at)}` : 'Nothing scheduled'}
          accent={isDark ? '#a5b4fc' : '#4f46e5'} />
        <StatCard isDark={isDark} label="Last backup"
          value={dbBackup.status.running ? 'Running…' : (dbBackup.status.lastError ? 'Failed' : (dbBackup.status.lastRunAt ? fmtAgo(dbBackup.status.lastRunAt, serverMs) : '—'))}
          sub={dbBackup.status.lastError || dbBackup.status.lastKey || 'No backup since the server started'}
          accent={dbBackup.status.lastError ? '#ef4444' : (isDark ? '#6ee7b7' : '#047857')} />
        <StatCard isDark={isDark} label="Report schedulers"
          value={`${activeReports.length} / ${clientReports.length}`}
          sub="enabled / configured" />
      </div>

      {/* ── Tab switcher — one tab per scheduler family ── */}
      <div style={{ background: 'var(--bg-raised)', borderRadius: 12, padding: 4, display: 'inline-flex', flexWrap: 'wrap', gap: 2 }}>
        <Button size="sm" variant={activeTab === 'backup' ? 'primary' : 'ghost'} onClick={() => setActiveTab('backup')}>
          🗄️ Database Backup
        </Button>
        <Button size="sm" variant={activeTab === 'reports' ? 'primary' : 'ghost'} onClick={() => setActiveTab('reports')}>
          📊 Client Reports ({clientReports.length})
        </Button>
      </div>

      {/* ── Tab 1: DB backup job ── */}
      {activeTab === 'backup' && (
      <SectionCard title="Database Backup" icon="🗄️"
        action={
          <div className="flex items-center gap-2">
            <Link to="/settings" className="text-xs font-medium" style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }}>Edit in Settings →</Link>
            <Button size="sm" loading={runningBackup} disabled={dbBackup.status.running}
              onClick={() => setConfirm({ type: 'backup' })}>
              Run backup now
            </Button>
          </div>
        }>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          <div>
            <InfoRow label="Status">
              {dbBackup.status.running
                ? <Badge variant="info">Running…</Badge>
                : (dbBackup.config.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="danger">Disabled</Badge>)}
            </InfoRow>
            <InfoRow label="Frequency">{FREQ_LABEL[dbBackup.config.frequency] || dbBackup.config.frequency}</InfoRow>
            <InfoRow label="Time">
              {fmtHHMM(`${pad2(dbBackup.config.hour)}:${pad2(dbBackup.config.minute)}`)}
              {dbBackup.config.frequency === 'weekly' ? ` on ${WEEKDAYS[dbBackup.config.weekday]}` : ''}
              {['every6h', 'every12h'].includes(dbBackup.config.frequency) ? ' (anchor)' : ''}
            </InfoRow>
            <InfoRow label="Retention">{dbBackup.config.retentionDays} days</InfoRow>
          </div>
          <div>
            <InfoRow label="Next run">
              {dbBackup.nextRunAt
                ? <span><strong style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }}>{fmtIn(untilMs(dbBackup.nextRunAt))}</strong> · {fmtWhen(dbBackup.nextRunAt)}</span>
                : '—'}
            </InfoRow>
            <InfoRow label="Last run">
              {dbBackup.status.lastRunAt ? `${fmtAgo(dbBackup.status.lastRunAt, serverMs)} · ${fmtWhen(dbBackup.status.lastRunAt)}` : 'None since the server started'}
            </InfoRow>
            {dbBackup.status.lastKey && (
              <InfoRow label="Last file"><span className="break-all text-xs">{dbBackup.status.lastKey}</span></InfoRow>
            )}
            {dbBackup.status.lastError && (
              <InfoRow label="Last error"><span style={{ color: '#ef4444' }}>{dbBackup.status.lastError}</span></InfoRow>
            )}
          </div>
        </div>
      </SectionCard>
      )}

      {/* ── Tab 2: Client report schedulers ── */}
      {activeTab === 'reports' && (
      <SectionCard title={`Client Daily Reports (${clientReports.length})`} icon="📊">
        {clientReports.length === 0 ? (
          <EmptyState icon="📊" title="No report schedulers configured"
            message="Enable a daily report on a client from the Clients page and it will appear here."
            action={<Link to="/clients"><Button variant="secondary" size="sm">Go to Clients</Button></Link>} />
        ) : (
          <div className="space-y-3">
            {clientReports.map(c => (
              <div key={c.clientId} className="rounded-xl p-4" style={{
                background: 'var(--surface-3)',
                border: `1px solid ${c.warning && c.enabled ? (isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d') : 'var(--border-subtle)'}`,
                opacity: c.enabled ? 1 : 0.65,
              }}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{c.clientName}</span>
                  {c.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="default">Paused</Badge>}
                  <Badge variant="info">{c.window === 'all' ? 'All-time window' : 'Last 24h window'}</Badge>
                  {c.time && (
                    <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
                      <ClockIcon /> {fmtHHMM(c.time)} daily
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    {c.enabled && c.nextRunAt && (
                      <span className="text-sm font-semibold" style={{ color: isDark ? '#a5b4fc' : '#4f46e5' }}>
                        {fmtIn(untilMs(c.nextRunAt))}
                        <span className="font-normal text-xs ml-1.5" style={{ color: 'var(--text-muted)' }}>{fmtWhen(c.nextRunAt)}</span>
                      </span>
                    )}
                    <Button size="sm" variant="secondary"
                      loading={sendingReport === c.clientId}
                      onClick={() => setConfirm({ type: 'report', client: c })}>
                      Send now
                    </Button>
                  </div>
                </div>

                {c.warning && (
                  <p className="text-xs mt-2 font-medium" style={{ color: isDark ? '#fcd34d' : '#b45309' }}>⚠ {c.warning}</p>
                )}

                <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-x-8 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <div className="py-0.5">
                    <span className="font-medium" style={{ color: 'var(--text-tertiary)' }}>Scope: </span>
                    {c.scope === 'all' ? 'All open requirements' : 'Selected requirements'}
                    {' — '}
                    {c.requirements.length === 0
                      ? 'none currently open'
                      : c.requirements.map(r => r.jobTitle).join(', ')}
                  </div>
                  <div className="py-0.5">
                    <span className="font-medium" style={{ color: 'var(--text-tertiary)' }}>Recipients: </span>
                    per-requirement contacts{c.fallbackRecipient ? `, else ${c.fallbackRecipient}` : ''}
                    {c.ccEmails.length > 0 && <span> · CC: {c.ccEmails.join(', ')}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      )}

      <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
        Schedulers tick once a minute on the server.
        {activeTab === 'backup'
          ? ' “Last run” is tracked in memory and resets when the backend restarts — the full backup history is under Settings → Database Backups.'
          : ' Reports are only emailed for requirements with activity in the window; a run with nothing to report sends nothing.'}
      </p>

      {/* ── Confirmations (both actions send real email / write to S3) ── */}
      <ConfirmModal
        isOpen={confirm?.type === 'backup'}
        danger={false}
        title="Run a database backup now?"
        confirmLabel="Yes, back up now"
        message="A full backup will be taken and uploaded to S3 immediately, in addition to the scheduled runs. This can take a minute."
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); runBackupNow(); }}
      />
      <ConfirmModal
        isOpen={confirm?.type === 'report'}
        danger={false}
        title={`Send ${confirm?.client?.clientName || 'this client'}'s report now?`}
        confirmLabel="Yes, send it"
        message={`The activity report will be emailed to the client's recipients immediately (per-requirement contacts${confirm?.client?.fallbackRecipient ? `, falling back to ${confirm.client.fallbackRecipient}` : ''}). Requirements with no activity in the window are skipped.`}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { const cl = confirm?.client; setConfirm(null); if (cl) sendReportNow(cl); }}
      />
    </div>
  );
}
